import { sv } from "@dhx/ts-common/dom";
import { getCoordinates } from "../helpers/circle";
import {
	checkPositions,
	getDefaultColor,
	getSizesSVGText,
	roundToTwoNumAfterPoint,
	verticalCenteredText,
} from "../helpers/common";
import { SvgElement } from "../types";
import NoScaleSeria from "./NoScaleSeria";

export default class Pie extends NoScaleSeria {
	// todo move to NoScaleSeria
	paint(width: number, height: number): SvgElement {
		const getPointAriaAttrs = (value, text, percent) => ({
			role: "graphics-symbol",
			"aria-roledescription": "piece of pie",
			"aria-label": `${text || ""}, ${value} (${roundToTwoNumAfterPoint(percent)}%)`,
		});
		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.text || ""}`,
		});
		const { subType, useLines, showText, showTextTemplate } = this.config;
		let radiusX;
		if (height > width) {
			radiusX = width / 2;
		} else {
			radiusX = height / 2;
		}
		const radiusY = radiusX * 0.5;
		const connector = radiusX / 5;

		const tooltipData = [];

		let currentPercent = -0.25;
		let svg = [];
		const links = [];
		const textPoints = [];
		this._points.forEach((item, index) => {
			const [percent, value, id, text, color] = item;
			const err = percent === 0 || percent === 1 ? -0.000001 : 0;

			const [startX, startY] = getCoordinates(currentPercent, radiusX, radiusY);
			const avPercent = currentPercent + percent / 2;
			let lineLength = avPercent < 0.25 ? 5 : -5;
			const middleLine = getCoordinates(avPercent, radiusX, radiusY);
			let delta = 0;
			if (avPercent > 0 && avPercent < 0.5) {
				delta = connector * Math.sin(2 * Math.PI * avPercent);
			}
			const linkStart = getCoordinates(avPercent, radiusX + 5 + delta, radiusY + 5 + delta);
			const linkEnd = getCoordinates(avPercent, radiusX + 30 + delta, radiusY + 30 + delta);
			const nextPercent = currentPercent + percent + err;
			const [endX, endY] = getCoordinates(nextPercent, radiusX, radiusY);
			const largeArcFlag = percent > 0.5 ? 1 : 0;
			const isRight = avPercent > -0.25 && avPercent < 0.25;
			const isUp = avPercent > 0.5 || avPercent < 0;
			const className = isRight ? "pie-value start-text" : "pie-value end-text";

			switch (this.config.subType) {
				case "basic": {
					const textPadding = 10;
					const dy = isUp ? -textPadding * 2 : textPadding;
					const className2 = isRight ? "donut-value start-text" : "donut-value end-text";

					const currentText = {
						text1: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							width: 0,
							height: 0,
							class: "",
						},
						text2: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							width: 0,
							height: 0,
							class: "",
						},
						changeSector: false,
						line: lineLength,
						right: isRight,
						dy: dy,
					};

					const text1 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							dx: useLines ? (lineLength / 2 + lineLength > 0 ? 10 : -10) : null,
							class: className,
							...getPointAriaAttrs(value, text, percent),
						},
						[verticalCenteredText(text.toString())]
					);
					const text2 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							dx: useLines ? (lineLength / 2 + lineLength > 0 ? 10 : -10) : null,
							class: className2,
							"aria-hidden": "true",
						},
						[verticalCenteredText(value.toString())]
					);
					const text3 = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);

					[currentText.text1.width, currentText.text1.height] = getSizesSVGText(text.toString(), {
						font: "normal 14px Roboto",
						lineHeight: 14,
					});
					[currentText.text2.width, currentText.text2.height] = getSizesSVGText(value.toString(), {
						font: "normal 12px Roboto",
						lineHeight: 12,
					});

					const textRadiusX = useLines ? radiusX + 30 + delta : radiusX + 5 + delta;
					const textRadiusY = useLines ? radiusY + 30 + delta : radiusY + 5 + delta;

					if (textPoints.length !== 0) {
						// const previosText = textPoints[index - 1];
						if (isRight) {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text1,
									previosText.text1,
									textRadiusX,
									textRadiusY,
									currentText
								);
								checkPositions(
									currentText.text1,
									previosText.text2,
									textRadiusX,
									textRadiusY,
									currentText
								);
							});
							if (currentText.text1.class) currentText.text2.class = currentText.text1.class;
						} else {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text2,
									previosText.text2,
									textRadiusX,
									textRadiusY,
									currentText
								);
								checkPositions(
									currentText.text2,
									previosText.text1,
									textRadiusX,
									textRadiusY,
									currentText
								);
							});
							if (currentText.text2.class) currentText.text1.class = currentText.text2.class;
						}
						text1.attrs.x = currentText.text1.x;
						text1.attrs.y = currentText.text1.y;
						text2.attrs.x = currentText.text2.x;
						text2.attrs.y = currentText.text2.y;
						if (currentText.text1.class || currentText.text2.class) {
							text1.attrs.class = currentText.text1.class;
							text2.attrs.class = currentText.text2.class;
						}
						lineLength = currentText.line;

						if (useLines) {
							linkEnd[0] = currentText.text1.x;
							linkEnd[1] = currentText.text1.y - dy;
						} else {
							linkStart[0] = currentText.text1.x;
							linkStart[1] = currentText.text1.y - dy;
						}
					}

					textPoints.push(currentText);
					links.push(text1, text2, text3);
					break;
				}
				case "valueOnly": {
					const valueText = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(value.toString())]
					);
					links.push(valueText);
					break;
				}
				case "percentOnly": {
					const percentText = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);
					links.push(percentText);
					break;
				}
			}

			if (useLines && subType === "basic") {
				links.push(
					sv("path", {
						d: `M${linkStart[0]} ${linkStart[1]} L${linkEnd[0]} ${linkEnd[1]} h ${lineLength}`,
						class: "pie-value-connector",
					})
				);
			}

			if ((showText || showTextTemplate) && showText !== false) {
				const textSvg = sv(
					"text",
					{
						x: middleLine[0] * 0.7,
						y: middleLine[1] * 0.7,
						class: "pie-inner-value",
						"aria-hidden": "true",
					},
					[
						showTextTemplate
							? verticalCenteredText(showTextTemplate(value))
							: verticalCenteredText(value.toString()),
					]
				);
				links.push(textSvg);
			}

			// 3d block
			let addition = "";
			if (currentPercent <= 0 && nextPercent >= 0.5) {
				addition = `M ${radiusX} 0 v ${connector} A ${radiusX} ${radiusY} 0 1 1 ${-radiusX} ${connector} v ${-connector}`;
			} else if (currentPercent <= 0 && nextPercent < 0.5) {
				addition = `M ${radiusX} 0 v ${connector} A ${radiusX} ${radiusY} 0 0 1 ${endX} ${endY +
					connector} v ${-connector}`;
			} else if (currentPercent > 0 && currentPercent <= 0.5 && nextPercent >= 0.5) {
				addition = `M ${startX} ${startY} v ${connector} A ${radiusX} ${radiusY} 0 0 1 ${-radiusX} ${connector} v ${-connector}`;
			} else if (currentPercent > 0 && nextPercent < 0.5) {
				addition = `M ${startX} ${startY} v ${connector} A ${radiusX} ${radiusY} 0 0 1 ${endX} ${endY +
					connector} v ${-connector}`;
			}
			if (addition) {
				const additionPath = sv("path", {
					_key: id + "__shadow__",
					d: addition,
					fill: color || getDefaultColor(index),
					onclick: [this._handlers.onclick, item[1], item[2]],
					class: "chart pie3d addition",
					stroke: "none",
					filter: "url(#shadow)",
					role: "presentation",
				});
				svg.push(additionPath);
			}
			// end 3d block
			const d = `M ${startX} ${startY} A ${radiusX} ${radiusY} 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;
			svg.push(
				sv("path", {
					d,
					_key: id,
					fill: color || getDefaultColor(index),
					stroke: "none",
					onclick: [this._handlers.onclick, item[1], item[2]],
					class: "chart pie3d",
					role: "presentation",
				})
			);

			if (this._points.length === 1) {
				tooltipData.push([width / 2, height / 2]);
			} else {
				tooltipData.push([middleLine[0] * 0.7 + width / 2, middleLine[1] * 0.7 + height / 2]);
			}

			currentPercent = nextPercent;
		});

		this._center = [width / 2, height / 2];
		this._tooltipData = tooltipData;

		svg = svg.concat(links);
		return sv(
			"g",
			{
				transform: `translate(${width / 2}, ${height / 2})`,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
			},
			svg
		);
	}
}
